/**
 * B1062 — Audit des messages d'erreur techniques (Eric 2026-09-04 SPEC STRICK PROD).
 *
 * Tableau de ce que la surveillance capte, avec la RÈGLE exacte qui déclenche
 * (ou non) une alerte. Sans ce tableau, un trou de surveillance ne se
 * découvre que pendant une panne — c'est précisément ce qui s'est passé les
 * 2 et 3 septembre 2026.
 *
 * Cinq familles ont été ajoutées par cet audit : erreurs navigateur,
 * saturation de sockets, pipeline média bloqué, services externes et clés
 * BYOK, refus 403/429 anormaux, base de données et stockage objet.
 */
import { ShieldCheck, Warning } from "@phosphor-icons/react";

export const MonitoringCoveragePanel = ({ coverage, onRunAudit, running }) => {
  const rows = coverage?.rows || [];

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-5"
      data-testid="monitor-coverage"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#002FA7]" />
            Audit des erreurs surveillées
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-3xl">
            Chaque famille d'erreurs du produit, avec la règle exacte qui déclenche une
            alerte. Un trou de surveillance ne doit pas se découvrir pendant une panne.
            {coverage?.new_in_b1062 ? (
              <>
                {" "}
                <strong>{coverage.new_in_b1062} familles</strong> ont été ajoutées par cet
                audit : elles produisaient déjà des erreurs, mais personne ne les lisait.
              </>
            ) : null}
          </p>
        </div>
        <button
          onClick={onRunAudit}
          disabled={running}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 shrink-0"
          data-testid="monitor-coverage-run-btn"
        >
          <Warning size={16} />
          {running ? "Audit en cours…" : "Auditer maintenant"}
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-zinc-200 p-3">
          <div className="text-xs uppercase tracking-wide text-zinc-500">Familles surveillées</div>
          <div className="text-xl font-bold text-zinc-900" data-testid="coverage-families">
            {coverage?.families ?? "—"}
          </div>
        </div>
        <div className="rounded-lg border border-zinc-200 p-3">
          <div className="text-xs uppercase tracking-wide text-zinc-500">En alerte</div>
          <div
            className={`text-xl font-bold ${
              coverage?.families_alerting ? "text-red-700" : "text-emerald-700"
            }`}
            data-testid="coverage-alerting"
          >
            {coverage?.families_alerting ?? "—"}
          </div>
        </div>
        <div className="rounded-lg border border-zinc-200 p-3">
          <div className="text-xs uppercase tracking-wide text-zinc-500">Stockage objet</div>
          <div className="text-sm font-semibold text-zinc-800 mt-1.5">
            {coverage?.storage_configured ? "canari actif" : "non configuré"}
          </div>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="monitor-coverage-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
              <th className="py-2 pr-3">État</th>
              <th className="py-2 pr-3">Famille d'erreurs</th>
              <th className="py-2 pr-3">Règle d'alerte</th>
              <th className="py-2 pr-3 text-right">24 h</th>
              <th className="py-2">Depuis</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.key}
                className="border-b border-zinc-100 align-top"
                data-testid={`coverage-row-${r.key}`}
              >
                <td className="py-2 pr-3">{r.open_incidents ? "❌" : "✅"}</td>
                <td className="py-2 pr-3 text-zinc-800">
                  {r.label}
                  {r.open_titles?.length ? (
                    <span className="block text-xs text-red-700 mt-0.5">
                      {r.open_titles.join(" · ")}
                    </span>
                  ) : null}
                  <span className="block text-[11px] text-zinc-400 font-mono">{r.source}</span>
                </td>
                <td className="py-2 pr-3 text-xs text-zinc-600 max-w-md">{r.rule}</td>
                <td className="py-2 pr-3 text-right font-mono text-xs text-zinc-600">
                  {r.events_24h == null ? "—" : r.events_24h}
                </td>
                <td className="py-2 text-xs">
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      r.since === "B1062"
                        ? "bg-emerald-50 text-emerald-800"
                        : "bg-zinc-100 text-zinc-600"
                    }`}
                  >
                    {r.since}
                  </span>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-zinc-500">
                  Chargement…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default MonitoringCoveragePanel;
